import React from 'react';
import { Link } from 'react-router-dom';
import { Package, TrendingUp, CheckCircle, Clock, AlertCircle, XCircle } from 'lucide-react';

const PreparativosCard = ({ preparativo, loading, selectedSeason }) => {
  const getStatusInfo = () => {
    if (!preparativo) {
      return {
        label: 'SIN DATOS',
        icon: AlertCircle,
        color: 'bg-gray-500',
        textColor: 'text-gray-700',
        bgColor: 'bg-gray-50',
      };
    }

    const estado = preparativo.estado || 'No iniciado';

    switch (estado) {
      case 'No iniciado':
        return {
          label: 'No Iniciado',
          icon: Clock,
          color: 'bg-gray-500',
          textColor: 'text-gray-700',
          bgColor: 'bg-gray-50',
        };
      case 'En proceso':
        return {
          label: 'En Proceso',
          icon: TrendingUp,
          color: 'bg-orange-500',
          textColor: 'text-orange-700',
          bgColor: 'bg-orange-50',
        };
      case 'Completado':
        return {
          label: 'Completado',
          icon: CheckCircle,
          color: 'bg-green-500',
          textColor: 'text-green-700',
          bgColor: 'bg-green-50',
        };
      case 'Cancelado':
        return {
          label: 'Cancelado',
          icon: XCircle,
          color: 'bg-red-500',
          textColor: 'text-red-700',
          bgColor: 'bg-red-50',
        };
      default:
        return {
          label: estado,
          icon: AlertCircle,
          color: 'bg-gray-500',
          textColor: 'text-gray-700',
          bgColor: 'bg-gray-50',
        };
    }
  };

  if (loading) {
    return (
      <div className="preparativos-card loading">
        <div className="stat-content">
          <div className="stat-info">
            <p className="stat-name">Preparativos de Exportación</p>
            <p className="stat-value">Cargando...</p>
          </div>
          <div className="stat-icon bg-gray-400">
            <Package size={24} />
          </div>
        </div>
        <style jsx>{`
          .preparativos-card {
            background: white;
            border-radius: var(--radius-lg);
            padding: var(--spacing-6);
            box-shadow: var(--shadow-sm);
            border: 1px solid var(--gray-200);
            transition: all 0.2s ease-in-out;
          }

          .preparativos-card.loading {
            pointer-events: none;
            opacity: 0.7;
          }

          @media (max-width: 768px) {
            .preparativos-card {
              padding: var(--spacing-3);
              border-radius: var(--radius-md);
            }
          }

          .stat-content {
            display: flex;
            justify-content: space-between;
            align-items: flex-start;
          }

          .stat-info {
            flex: 1;
          }

          .stat-name {
            font-size: var(--font-size-sm);
            color: var(--gray-600);
            margin: 0 0 var(--spacing-2) 0;
          }

          @media (max-width: 768px) {
            .stat-name {
              font-size: 0.625rem;
              line-height: 1;
            }
          }

          .stat-value {
            font-size: var(--font-size-lg);
            font-weight: 600;
            color: var(--gray-400);
            margin: 0;
          }

          .stat-icon {
            width: 48px;
            height: 48px;
            border-radius: var(--radius-lg);
            display: flex;
            align-items: center;
            justify-content: center;
            color: white;
            flex-shrink: 0;
          }

          @media (max-width: 768px) {
            .stat-icon {
              width: 32px;
              height: 32px;
            }

            .stat-icon svg {
              width: 16px;
              height: 16px;
            }
          }
        `}</style>
      </div>
    );
  }

  if (!preparativo) {
    const statusInfo = getStatusInfo();
    const StatusIcon = statusInfo.icon;

    return (
      <Link to="/export/preparativos" className="preparativos-card">
        <div className="stat-content">
          <div className="stat-info">
            <p className="stat-name">Preparativos de Exportación</p>
            <div className="status-container">
              <span className={`status-badge ${statusInfo.bgColor} ${statusInfo.textColor}`}>
                <StatusIcon size={16} />
                {statusInfo.label}
              </span>
            </div>
          </div>
          <div className={`stat-icon ${statusInfo.color}`}>
            <Package size={24} />
          </div>
        </div>
        <style jsx>{`
          .preparativos-card {
            background: white;
            border-radius: var(--radius-lg);
            padding: var(--spacing-6);
            box-shadow: var(--shadow-sm);
            border: 1px solid var(--gray-200);
            text-decoration: none;
            color: inherit;
            transition: all 0.2s ease-in-out;
            display: block;
          }

          @media (max-width: 768px) {
            .preparativos-card {
              padding: var(--spacing-3);
              border-radius: var(--radius-md);
            }
          }

          .preparativos-card:hover {
            transform: translateY(-2px);
            box-shadow: var(--shadow-lg);
          }

          .stat-content {
            display: flex;
            justify-content: space-between;
            align-items: flex-start;
          }

          .stat-info {
            flex: 1;
          }

          .stat-name {
            font-size: var(--font-size-sm);
            color: var(--gray-600);
            margin: 0 0 var(--spacing-2) 0;
          }

          @media (max-width: 768px) {
            .stat-name {
              font-size: 0.625rem;
              line-height: 1;
            }
          }

          .status-container {
            margin-bottom: var(--spacing-3);
          }

          .status-badge {
            display: inline-flex;
            align-items: center;
            gap: var(--spacing-2);
            padding: var(--spacing-2) var(--spacing-3);
            border-radius: var(--radius-md);
            font-size: var(--font-size-sm);
            font-weight: 600;
          }

          @media (max-width: 768px) {
            .status-badge {
              font-size: 0.625rem;
              padding: 0.25rem 0.5rem;
              gap: 0.25rem;
            }

            .status-badge svg {
              width: 12px;
              height: 12px;
            }
          }

          .stat-icon {
            width: 48px;
            height: 48px;
            border-radius: var(--radius-lg);
            display: flex;
            align-items: center;
            justify-content: center;
            color: white;
            flex-shrink: 0;
          }

          @media (max-width: 768px) {
            .stat-icon {
              width: 32px;
              height: 32px;
            }

            .stat-icon svg {
              width: 16px;
              height: 16px;
            }
          }
        `}</style>
      </Link>
    );
  }

  const statusInfo = getStatusInfo();
  const StatusIcon = statusInfo.icon;
  const avance = preparativo?.avance !== undefined ? preparativo.avance : 0;
  const showProgress = preparativo && preparativo.estado === 'En proceso' && avance > 0;

  return (
    <Link to="/export/preparativos" className="preparativos-card">
      <div className="stat-content">
        <div className="stat-info">
          <p className="stat-name">Preparativos de Exportación</p>
          <div className="status-container">
            <span className={`status-badge ${statusInfo.bgColor} ${statusInfo.textColor}`}>
              <StatusIcon size={16} />
              {statusInfo.label}
            </span>
          </div>
          {showProgress && (
            <div className="progress-section">
              <div className="progress-header">
                <span className="progress-label">Avance</span>
                <span className="progress-value">{avance}%</span>
              </div>
              <div className="progress-bar-container">
                <div 
                  className="progress-bar-fill"
                  style={{ width: `${avance}%` }}
                />
              </div>
            </div>
          )}
        </div>
        <div className={`stat-icon ${statusInfo.color}`}>
          <Package size={24} />
        </div>
      </div>
      {preparativo && (
        <div className="stat-footer">
          <TrendingUp size={16} />
          <span>Ver detalles del preparativo</span>
        </div>
      )}

      <style jsx>{`
        .preparativos-card {
          background: white;
          border-radius: var(--radius-lg);
          padding: var(--spacing-6);
          box-shadow: var(--shadow-sm);
          border: 1px solid var(--gray-200);
          text-decoration: none;
          color: inherit;
          transition: all 0.2s ease-in-out;
          display: block;
        }

        @media (max-width: 768px) {
          .preparativos-card {
            padding: var(--spacing-3);
            border-radius: var(--radius-md);
          }
        }

        .preparativos-card:hover {
          transform: translateY(-2px);
          box-shadow: var(--shadow-lg);
        }

        .stat-content {
          display: flex;
          justify-content: space-between;
          align-items: flex-start;
        }
        
        .preparativos-card:has(.stat-footer) .stat-content {
          margin-bottom: var(--spacing-4);
        }

        .stat-info {
          flex: 1;
        }

        .stat-name {
          font-size: var(--font-size-sm);
          color: var(--gray-600);
          margin: 0 0 var(--spacing-2) 0;
        }

        @media (max-width: 768px) {
          .stat-name {
            font-size: 0.625rem;
            line-height: 1;
          }
        }

        .status-container {
          margin-bottom: var(--spacing-3);
        }

        .status-badge {
          display: inline-flex;
          align-items: center;
          gap: var(--spacing-2);
          padding: var(--spacing-2) var(--spacing-3);
          border-radius: var(--radius-md);
          font-size: var(--font-size-sm);
          font-weight: 600;
        }

        @media (max-width: 768px) {
          .status-badge {
            font-size: 0.625rem;
            padding: 0.25rem 0.5rem;
            gap: 0.25rem;
          }

          .status-badge svg {
            width: 12px;
            height: 12px;
          }
        }

        .progress-section {
          margin-top: var(--spacing-3);
        }

        .progress-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: var(--spacing-2);
        }

        .progress-label {
          font-size: var(--font-size-sm);
          color: var(--gray-600);
          font-weight: 500;
        }

        @media (max-width: 768px) {
          .progress-label {
            font-size: 0.625rem;
          }
        }

        .progress-value {
          font-size: var(--font-size-lg);
          font-weight: 700;
          color: var(--gray-900);
        }

        @media (max-width: 768px) {
          .progress-value {
            font-size: 1rem;
          }
        }

        .progress-bar-container {
          width: 100%;
          height: 8px;
          background-color: var(--gray-200);
          border-radius: var(--radius-full);
          overflow: hidden;
        }

        @media (max-width: 768px) {
          .progress-bar-container {
            height: 6px;
          }
        }

        .progress-bar-fill {
          height: 100%;
          background: linear-gradient(90deg, var(--orange-500), var(--orange-600));
          border-radius: var(--radius-full);
          transition: width 0.3s ease-in-out;
        }

        .stat-icon {
          width: 48px;
          height: 48px;
          border-radius: var(--radius-lg);
          display: flex;
          align-items: center;
          justify-content: center;
          color: white;
          flex-shrink: 0;
        }

        @media (max-width: 768px) {
          .stat-icon {
            width: 32px;
            height: 32px;
          }

          .stat-icon svg {
            width: 16px;
            height: 16px;
          }
        }

        .stat-footer {
          display: flex;
          align-items: center;
          gap: var(--spacing-2);
          color: var(--primary-blue);
          font-size: var(--font-size-sm);
          font-weight: 500;
          padding-top: var(--spacing-3);
          border-top: 1px solid var(--gray-100);
        }

        @media (max-width: 768px) {
          .stat-footer {
            font-size: 0.625rem;
            gap: var(--spacing-1);
          }

          .stat-footer svg {
            width: 12px;
            height: 12px;
          }

          .stat-footer span {
            display: none;
          }
        }
      `}</style>
    </Link>
  );
};

export default PreparativosCard;
